import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import PageHero from "@/components/common/PageHero";
import PageTransition from "@/components/layout/PageTransition";
import AnimatedSection from "@/components/common/AnimatedSection";
import LightboxModal from "@/components/common/LightboxModal";
import { pageHeroImages, getWhatsappUrl } from "@/config/site";
import { api } from "@/lib/api";

export default function CaseStudiesPage() {
  const [cases, setCases] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const loadCases = async () => {
      try {
        const response = await api.get("/public/case-studies");
        setCases(response.data);
      } catch {
        toast.error("Unable to load case studies");
      }
    };

    loadCases();
  }, []);

  return (
    <PageTransition testId="case-studies-page">
      <div className={selectedImage ? "blur-[1px]" : ""}>
        <PageHero
          title="Patient Transformations"
          subtitle="Real journeys in implants, smile aesthetics, and restorative rehabilitation."
          image={pageHeroImages.caseStudies}
          testId="case-studies-page-hero"
        />

        <div className="mx-auto max-w-7xl space-y-14 px-6 py-16 md:px-10 md:py-24">
          <AnimatedSection testId="case-studies-grid-section">
            <h2
              className="font-[Playfair_Display] text-3xl text-[#1a1a1a] md:text-5xl"
              data-testid="case-studies-grid-title"
            >
              Before & After Case Highlights
            </h2>
            <p
              className="mt-3 max-w-3xl font-[Manrope] text-sm text-[#555555]"
              data-testid="case-studies-grid-subtitle"
            >
              Explore a curated set of transformation stories demonstrating careful
              planning, precision treatment, and patient confidence.
            </p>

            <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {cases.map((item, index) => (
                <motion.article
                  key={item.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.55, delay: index * 0.06 }}
                  className="overflow-hidden rounded-2xl border border-[#f2e6c9] bg-white shadow-[0_10px_30px_rgba(0,0,0,0.05)]"
                  data-testid={`case-study-card-${item.id}`}
                >
                  <div className="grid grid-cols-2 gap-0">
                    <button
                      onClick={() =>
                        setSelectedImage({
                          image: item.before_image,
                          title: `${item.title} · Before`,
                        })
                      }
                      className="group relative aspect-[3/4] overflow-hidden"
                      data-testid={`case-study-before-button-${item.id}`}
                    >
                      <img
                        src={item.before_image}
                        alt={`${item.title} before`}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                        data-testid={`case-study-before-image-${item.id}`}
                      />
                      <span
                        className="absolute left-2 top-2 rounded-full bg-[#1a1a1a]/80 px-2 py-1 font-[Manrope] text-[10px] uppercase tracking-wider text-white"
                        data-testid={`case-study-before-label-${item.id}`}
                      >
                        Before
                      </span>
                    </button>
                    <button
                      onClick={() =>
                        setSelectedImage({
                          image: item.after_image,
                          title: `${item.title} · After`,
                        })
                      }
                      className="group relative aspect-[3/4] overflow-hidden"
                      data-testid={`case-study-after-button-${item.id}`}
                    >
                      <img
                        src={item.after_image}
                        alt={`${item.title} after`}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                        data-testid={`case-study-after-image-${item.id}`}
                      />
                      <span
                        className="absolute left-2 top-2 rounded-full bg-[#c5a059]/90 px-2 py-1 font-[Manrope] text-[10px] uppercase tracking-wider text-white"
                        data-testid={`case-study-after-label-${item.id}`}
                      >
                        After
                      </span>
                    </button>
                  </div>

                  <div className="p-5">
                    <p
                      className="font-[Manrope] text-xs uppercase tracking-[0.16em] text-[#8b7a55]"
                      data-testid={`case-study-category-${item.id}`}
                    >
                      {item.category}
                    </p>
                    <h3
                      className="mt-2 font-[Playfair_Display] text-2xl text-[#1a1a1a]"
                      data-testid={`case-study-title-${item.id}`}
                    >
                      {item.title}
                    </h3>
                    <p
                      className="mt-3 font-[Manrope] text-sm text-[#555555]"
                      data-testid={`case-study-summary-${item.id}`}
                    >
                      {item.summary}
                    </p>
                    <Link
                      to={`/case-studies/${item.id}`}
                      className="mt-4 inline-flex font-[Manrope] text-sm font-semibold text-[#b08d45] underline-offset-4 transition-colors hover:text-[#8c6a2b] hover:underline"
                      data-testid={`case-study-know-more-link-${item.id}`}
                    >
                      Know More
                    </Link>
                  </div>
                </motion.article>
              ))}
            </div>
          </AnimatedSection>

          <AnimatedSection testId="case-studies-cta-section">
            <div className="rounded-2xl bg-[#1a1a1a] px-8 py-10 text-center">
              <h3
                className="font-[Playfair_Display] text-3xl text-white"
                data-testid="case-studies-cta-title"
              >
                Want a similar transformation?
              </h3>
              <p
                className="mt-3 font-[Manrope] text-sm text-[#f2e6c9]"
                data-testid="case-studies-cta-text"
              >
                Share your dental goals with us and receive a personalized care
                pathway.
              </p>
              <a
                href={getWhatsappUrl()}
                target="_blank"
                rel="noreferrer"
                className="mt-6 inline-flex rounded-full bg-[#c5a059] px-6 py-3 font-[Manrope] text-xs font-semibold uppercase tracking-[0.16em] text-white hover:bg-[#b08d45]"
                data-testid="case-studies-cta-button"
              >
                Start Consultation
              </a>
            </div>
          </AnimatedSection>
        </div>
      </div>

      <LightboxModal
        isOpen={Boolean(selectedImage)}
        image={selectedImage?.image}
        title={selectedImage?.title}
        onClose={() => setSelectedImage(null)}
      />
    </PageTransition>
  );
}
